import React, { useState, useEffect, useRef } from "react";

const FAMILY_DOCS_ID = "ab3eed5d-75db-4fb6-b6a9-01c2996201ba";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

const CATEGORY_OPTIONS = ["Personal", "Family", "Kids", "Carissa Lyn Photography", "Summerlyn Studios", "Work", "Church", "House", "old"];
const TAG_OPTIONS = ["Insurance", "Medical", "Dental", "Kids", "Adam", "Kimmy", "CLP", "Products", "Networking", "Livestream", "class", "salesforce", "Dubsado", "School", "Call Log", "SLS", "Spring Train", "Gym", "Fitness", "Financial", "Personal", "Organization", "House", "polka dot powerhouse"];

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function TagPills({ tags, max = 3 }) {
  if (!tags?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {tags.slice(0, max).map(t => (
        <span key={t} style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 20, background: "var(--cream-hover)", color: "var(--muted)", border: "1px solid var(--border)" }}>{t}</span>
      ))}
      {tags.length > max && <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>+{tags.length - max}</span>}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div style={{ display: "flex", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
      <div className="skeleton" style={{ width: 44, height: 44, borderRadius: 11, flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="skeleton skeleton-line-long" style={{ height: 15 }} />
        <div className="skeleton skeleton-line-short" style={{ height: 12 }} />
      </div>
    </div>
  );
}

function RichText({ text, urls }) {
  if (!urls || urls.length === 0) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return <span>{text}</span>;
    return <span>{parts.map((part, i) => urlRegex.test(part) ? <a key={i} href={part} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{part}</a> : <span key={i}>{part}</span>)}</span>;
  }
  return <span>{urls.map((u, i) => <a key={i} href={u.url} target="_blank" rel="noreferrer" style={{ color: "var(--sage)", textDecoration: "underline" }}>{u.text}</a>)}</span>;
}

function MultiPicker({ options, selected, onChange, placeholder, allowCreate }) {
  const [search, setSearch] = useState("");
  const trimmed = search.trim();
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = options.some(o => o.toLowerCase() === trimmed.toLowerCase()) || selected.some(s => s.toLowerCase() === trimmed.toLowerCase());
  const showCreate = allowCreate && trimmed.length > 0 && !exactMatch;
  const toggle = (opt) => onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);
  const handleCreate = () => { if (!trimmed || exactMatch) return; onChange([...selected, trimmed]); setSearch(""); };
  return (
    <div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder || "Search..."}
        style={{ width: "100%", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", marginBottom: 8, background: "var(--cream-hover)", color: "var(--charcoal)" }} />
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map(s => (
            <span key={s} onClick={() => toggle(s)} style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "var(--sage)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
              {s} <span style={{ opacity: 0.8 }}>×</span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto" }}>
        {showCreate && (
          <span onClick={handleCreate} style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "var(--sage-muted)", color: "var(--sage)", border: "1.5px dashed var(--sage)", cursor: "pointer" }}>+ Create “{trimmed}”</span>
        )}
        {filtered.filter(o => !selected.includes(o)).map(opt => (
          <span key={opt} onClick={() => toggle(opt)} style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "var(--cream-hover)", color: "var(--charcoal)", border: "1px solid var(--border)", cursor: "pointer" }}>{opt}</span>
        ))}
      </div>
    </div>
  );
}

function TagFilter({ allTags, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const isFiltered = selected.length < allTags.length;
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const filtered = allTags.filter(t => t.toLowerCase().includes(search.toLowerCase()));
  const toggle = (tag) => onChange(selected.includes(tag) ? selected.filter(x => x !== tag) : [...selected, tag]);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${isFiltered ? "var(--charcoal)" : "var(--border)"}`, background: isFiltered ? "var(--charcoal)" : "var(--white)", color: isFiltered ? "#fff" : "var(--charcoal)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        🏷️ Tags
        {isFiltered && <span style={{ background: "#fff", color: "var(--charcoal)", borderRadius: 10, fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>{selected.length}/{allTags.length}</span>}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", minWidth: 220, overflow: "hidden" }}>
          <div style={{ padding: "10px 12px 6px" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tags…"
              style={{ width: "100%", border: "1px solid var(--border)", borderRadius: 7, padding: "6px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", background: "var(--cream-hover)", color: "var(--charcoal)" }} />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {filtered.map((tag, i) => {
              const checked = selected.includes(tag);
              return (
                <div key={tag} onClick={() => toggle(tag)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none", background: checked ? "var(--cream-hover)" : "transparent" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, border: checked ? "none" : "2px solid var(--border)", background: checked ? "var(--sage)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {checked && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: "var(--charcoal)" }}>{tag}</span>
                </div>
              );
            })}
            {filtered.length === 0 && <div style={{ padding: "12px 14px", fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>No tags found</div>}
          </div>
          <div style={{ display: "flex", borderTop: "1px solid var(--border)" }}>
            <button onClick={() => { onChange(allTags); setOpen(false); }} style={{ flex: 1, padding: "9px 0", fontSize: 12, color: "var(--sage)", fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>Select all</button>
            <button onClick={() => { onChange([]); setOpen(false); }} style={{ flex: 1, padding: "9px 0", fontSize: 12, color: "var(--muted)", fontWeight: 700, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>Deselect all</button>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteMetaSheet({ note, onClose, onSaved }) {
  const [tags, setTags] = useState(note.tags || []);
  const [category, setCategory] = useState(note.category || []);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: note.id, action: "updateNoteMeta", value: { tags, category } }) });
    setSaving(false);
    onSaved({ ...note, tags, category });
  };
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300 }} />
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 301, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <button onClick={onClose} style={sheetBtn}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>Edit Tags & Category</span>
          <button onClick={handleSave} disabled={saving} style={{ ...sheetBtn, color: saving ? "var(--muted)" : "var(--sage)", fontWeight: 800 }}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div><div style={sheetLabel}>Category</div><MultiPicker options={CATEGORY_OPTIONS} selected={category} onChange={setCategory} placeholder="Search categories…" /></div>
          <div><div style={sheetLabel}>Tags</div><MultiPicker options={TAG_OPTIONS} selected={tags} onChange={setTags} placeholder="Search or create tag…" allowCreate /></div>
        </div>
      </div>
    </>
  );
}

export function NoteDetail({ note: initialNote, onBack, onTabTap, onTogglePin, activeTab }) {
  const [note, setNote] = useState(initialNote);
  const [blocks, setBlocks] = useState([]);
  const [isSimple, setIsSimple] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pinned, setPinned] = useState(initialNote.pinned || false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMeta, setShowMeta] = useState(false);

  const loadBlocks = () => {
    setLoading(true);
    fetch(`/.netlify/functions/notion?type=blocks&pageId=${note.id}`)
      .then(r => r.json()).then(d => { setBlocks(d.blocks || []); setIsSimple(!!d.isSimple); setEditText(d.blocks?.[0]?.text || ""); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadBlocks(); }, [note.id]);

  const handleTogglePin = async () => {
    const newVal = !pinned; setPinned(newVal); onTogglePin?.(note.id, newVal);
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: note.id, action: "toggleNotePinned", value: newVal }) });
  };
  const handleSaveText = async () => {
    setSaving(true);
    await fetch("/.netlify/functions/notion", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pageId: note.id, action: "updateNoteText", value: { text: editText } }) });
    setSaving(false); setEditing(false); loadBlocks();
  };

  const activeTabId = activeTab || "essential";
  return (
    <div className="app">
      <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={backBtn}>‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 600, color: "var(--charcoal)", lineHeight: 1.2 }}>{note.name}</div>
          {note.lastEdited && <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>Updated {formatDate(note.lastEdited)}</div>}
        </div>
        <button onClick={handleTogglePin} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", opacity: pinned ? 1 : 0.4 }}>📌</button>
        <a href={note.notionUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 800, color: "var(--sage)", textDecoration: "none" }}>Notion ↗</a>
      </header>
      <div className="scroll-body" style={{ padding: "12px 16px" }}>
        <div style={{ background: "var(--cream-hover)", borderRadius: 12, padding: "12px 14px", marginBottom: 12, border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)" }}>Details</div>
            <button onClick={() => setShowMeta(true)} style={{ background: "none", border: "none", fontSize: 12, fontWeight: 700, color: "var(--sage)", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
          </div>
          {note.notebookName && <div style={{ marginBottom: 6 }}><span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--muted)", marginRight: 6 }}>Notebook</span><span style={{ fontSize: 13, fontWeight: 600, color: "var(--charcoal)" }}>📒 {note.notebookName}</span></div>}
          {note.category?.length > 0 && <div style={{ marginBottom: 6 }}><span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--muted)", marginRight: 6 }}>Category</span><span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>{note.category.map(c => <span key={c} style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "var(--sage-muted)", color: "var(--sage)" }}>{c}</span>)}</span></div>}
          {note.tags?.length > 0 && <div><span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--muted)", marginRight: 6 }}>Tags</span><span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>{note.tags.map(t => <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "var(--cream-hover)", color: "var(--muted)", border: "1px solid var(--border)" }}>{t}</span>)}</span></div>}
          {!note.notebookName && !note.category?.length && !note.tags?.length && <div style={{ fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>No tags or categories yet. <button onClick={() => setShowMeta(true)} style={{ background: "none", border: "none", color: "var(--sage)", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Add some</button></div>}
        </div>
        {loading ? <div style={{ padding: 20, textAlign: "center", color: "var(--muted)", fontSize: 14 }}>Loading…</div>
          : editing ? (
            <div style={{ background: "var(--white)", borderRadius: 14, border: "1.5px solid var(--sage)", padding: "14px 18px" }}>
              <textarea autoFocus rows={10} value={editText} onChange={e => setEditText(e.target.value)} style={{ width: "100%", border: "none", outline: "none", fontSize: 15, color: "var(--charcoal)", fontFamily: "inherit", lineHeight: 1.6, resize: "vertical", background: "transparent" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={handleSaveText} disabled={saving} style={{ padding: "9px 18px", borderRadius: 9, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setEditing(false)} style={{ padding: "9px 18px", borderRadius: 9, background: "var(--cream-hover)", border: "none", color: "var(--muted)", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              </div>
            </div>
          ) : blocks.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
              <div style={{ fontSize: 14, color: "var(--muted)", fontWeight: 600, marginBottom: 12 }}>No content yet</div>
              <button onClick={() => { setEditText(""); setEditing(true); }} style={{ fontSize: 13, fontWeight: 800, color: "var(--sage)", background: "var(--sage-muted)", border: "none", borderRadius: 9, padding: "9px 18px", cursor: "pointer", fontFamily: "inherit" }}>Add a note</button>
            </div>
          ) : (
            <>
              <div style={{ background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
                {blocks.map((b, i) => (
                  <div key={i} style={{ fontSize: b.type === "heading_1" ? 18 : b.type === "heading_2" ? 16 : 15, fontWeight: b.type?.startsWith("heading") ? 700 : 500, fontFamily: b.type?.startsWith("heading") ? "'Playfair Display', serif" : "inherit", color: "var(--charcoal)", lineHeight: 1.6, paddingLeft: b.type === "bulleted_list_item" ? 14 : 0, position: "relative" }}>
                    {b.type === "bulleted_list_item" && <span style={{ position: "absolute", left: 0, color: "var(--sage)", fontWeight: 800 }}>•</span>}
                    <RichText text={b.text} urls={b.urls} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                {isSimple ? <button onClick={() => setEditing(true)} style={{ fontSize: 13, fontWeight: 700, color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Edit note</button>
                  : <a href={note.notionUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 700, color: "var(--muted)", textDecoration: "none" }}>This note has formatting — edit in Notion ↗</a>}
              </div>
            </>
          )}
      </div>
      <nav style={bottomNav}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={navItem}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: t.id === activeTabId ? 800 : 700, color: t.id === activeTabId ? "var(--sage)" : "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            {t.id === activeTabId && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--sage)", marginTop: 1 }} />}
          </div>
        ))}
      </nav>
      {showMeta && <NoteMetaSheet note={note} onClose={() => setShowMeta(false)} onSaved={(updated) => { setNote(updated); setShowMeta(false); }} />}
    </div>
  );
}

export function NotebookView({ notebook, category, categories, onBack, onNoteTap, onTabTap, activeTab }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteName, setNewNoteName] = useState("");
  const [creating, setCreating] = useState(false);
  const [addError, setAddError] = useState("");
  const [tagFilter, setTagFilter] = useState(null);

  useEffect(() => {
    let url = `/.netlify/functions/notion?type=notes&notebookId=${notebook.id}`;
    if (categories) url += `&categories=${encodeURIComponent(categories)}`;
    else if (category) url += `&category=${encodeURIComponent(category)}`;
    fetch(url).then(r => r.json()).then(d => {
      const items = d.items || [];
      setNotes(items);
      const allTags = [...new Set(items.flatMap(n => n.tags || []))];
      setTagFilter(allTags);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [notebook.id, category, categories]);

  const handleCreateNote = async () => {
    if (!newNoteName.trim()) { setAddError("Name is required"); return; }
    setCreating(true); setAddError("");
    try {
      const res = await fetch("/.netlify/functions/notion", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "createNote", value: { name: newNoteName.trim(), notebookId: notebook.id } }) });
      const data = await res.json();
      if (data.item) { setNotes(prev => [data.item, ...prev]); setNewNoteName(""); setShowAddNote(false); onNoteTap(data.item); }
      else setAddError("Failed to create note");
    } catch { setAddError("Something went wrong"); }
    finally { setCreating(false); }
  };

  const allTags = [...new Set(notes.flatMap(n => n.tags || []))];
  const filterActive = tagFilter !== null && tagFilter.length < allTags.length;
  const visibleNotes = filterActive ? notes.filter(n => !n.tags?.length || n.tags.some(t => tagFilter.includes(t))) : notes;
  const pinnedNotes = visibleNotes.filter(n => n.pinned);
  const unpinnedNotes = visibleNotes.filter(n => !n.pinned);
  const icon = notebook.pinned ? "📁" : "📒";
  const activeTabId = activeTab || "essential";

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 0", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <button onClick={onBack} style={backBtn}>‹</button>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: "var(--sage-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "var(--charcoal)" }}>{notebook.name}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, marginTop: 2 }}>{visibleNotes.length} notes{filterActive ? " (filtered)" : ""}</div>
            </div>
          </div>
          {allTags.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 0 12px" }}>
              <TagFilter allTags={allTags} selected={tagFilter || allTags} onChange={setTagFilter} />
              {filterActive && <button onClick={() => setTagFilter(allTags)} style={{ background: "none", border: "none", fontSize: 12, color: "var(--muted)", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>}
            </div>
          )}
        </header>
        <div className="scroll-body" style={{ padding: 0 }}>
          <button onClick={() => setShowAddNote(true)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", cursor: "pointer", color: "var(--sage)", fontSize: 14, fontWeight: 800, background: "var(--white)", border: "none", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", width: "100%", textAlign: "left", fontFamily: "inherit" }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--sage-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>+</div>
            New Note
          </button>
          {pinnedNotes.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "10px 20px 4px" }}>📌 Pinned</div>
              <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
                {pinnedNotes.map(note => (
                  <div key={note.id} onClick={() => onNoteTap(note)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                    <div style={{ fontSize: 16, marginTop: 2 }}>📌</div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: "var(--charcoal)" }}>{note.name}</div><TagPills tags={note.tags} />{note.lastEdited && <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>Updated {formatDate(note.lastEdited)}</div>}</div>
                    <span style={{ color: "var(--border)", fontSize: 18 }}>›</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)", marginTop: pinnedNotes.length > 0 ? 8 : 0 }}>
            {loading ? <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
              : unpinnedNotes.length === 0 && pinnedNotes.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">📝</div><div className="empty-text">{filterActive ? "No notes match this filter" : "No notes yet"}</div></div>
              ) : unpinnedNotes.map(note => (
                <div key={note.id} onClick={() => onNoteTap(note)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--border)", cursor: "pointer" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--sage)", flexShrink: 0, marginTop: 6 }} />
                  <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: "var(--charcoal)" }}>{note.name}</div><TagPills tags={note.tags} />{note.lastEdited && <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 3 }}>Updated {formatDate(note.lastEdited)}</div>}</div>
                  <span style={{ color: "var(--border)", fontSize: 18 }}>›</span>
                </div>
              ))}
          </div>
        </div>
        <nav style={bottomNav}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={navItem}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: t.id === activeTabId ? 800 : 700, color: t.id === activeTabId ? "var(--sage)" : "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
              {t.id === activeTabId && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--sage)", marginTop: 1 }} />}
            </div>
          ))}
        </nav>
      </div>
      {showAddNote && (
        <>
          <div onClick={() => setShowAddNote(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }} />
          <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 201, paddingBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
              <button onClick={() => setShowAddNote(false)} style={sheetBtn}>Cancel</button>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>New Note</span>
              <button onClick={handleCreateNote} disabled={creating} style={{ ...sheetBtn, color: creating ? "var(--muted)" : "var(--sage)", fontWeight: 800 }}>{creating ? "Creating…" : "Create"}</button>
            </div>
            {addError && <div style={{ padding: "8px 20px", color: "var(--rose)", fontSize: 13, fontWeight: 600 }}>{addError}</div>}
            <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
              <div style={sheetLabel}>Title</div>
              <input autoFocus value={newNoteName} onChange={e => setNewNoteName(e.target.value)} placeholder="Note title…" style={{ width: "100%", border: "none", outline: "none", fontSize: 16, fontWeight: 600, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }} />
            </div>
            <div style={{ padding: "14px 20px" }}>
              <div style={sheetLabel}>Notebook</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--charcoal)" }}>{notebook.pinned ? "📁" : "📒"} {notebook.name}</div>
            </div>
            <div style={{ padding: "0 20px", fontSize: 13, color: "var(--muted)", fontWeight: 500 }}>Note will open in Notion after creation so you can add content.</div>
          </div>
        </>
      )}
    </>
  );
}

// EssentialInfoPage accepts optional onNotebookTap/onNoteTap to push into App nav stack.
// Falls back to internal state when those props aren't provided.
export default function EssentialInfoPage({ onTabTap, onNotebookTap, onNoteTap }) {
  const [notebooks, setNotebooks] = useState([]);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Internal fallback state (used when no external nav props provided)
  const [selectedNotebook, setSelectedNotebook] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);

  const useExternalNav = !!(onNotebookTap || onNoteTap);

  useEffect(() => {
    Promise.all([
      fetch("/.netlify/functions/notion?type=notebooks").then(r => r.json()),
      fetch("/.netlify/functions/notion?type=pinnedNotes").then(r => r.json()),
    ]).then(([nbData, pinData]) => {
      setNotebooks(nbData.items || []);
      setPinnedNotes(pinData.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleNotebookTap = (nb) => useExternalNav ? onNotebookTap(nb) : setSelectedNotebook(nb);
  const handleNoteTap = (note) => useExternalNav ? onNoteTap(note) : setSelectedNote(note);

  if (!useExternalNav && selectedNote) return (
    <NoteDetail note={selectedNote} onBack={() => setSelectedNote(null)} onTabTap={onTabTap} activeTab="essential"
      onTogglePin={(id, val) => setPinnedNotes(prev => val ? [...prev, selectedNote] : prev.filter(n => n.id !== id))} />
  );
  if (!useExternalNav && selectedNotebook) return (
    <NotebookView notebook={selectedNotebook} onBack={() => setSelectedNotebook(null)}
      onNoteTap={(note) => setSelectedNote(note)} onTabTap={onTabTap} activeTab="essential" />
  );

  const pinned = notebooks.find(n => n.pinned);
  const rest = notebooks.filter(n => !n.pinned);

  return (
    <div className="app">
      <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>
          Essential <span style={{ position: "relative" }}>Info<span style={{ position: "absolute", bottom: -2, left: 0, width: "100%", height: 2, background: "var(--sage)", borderRadius: 2 }} /></span>
        </div>
      </header>
      <div className="scroll-body" style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? <><SkeletonRow /><SkeletonRow /><SkeletonRow /></> : (
          <>
            {pinnedNotes.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px" }}>📌 Pinned Notes</div>
                {pinnedNotes.map(note => (
                  <div key={note.id} onClick={() => handleNoteTap(note)} style={{ background: "var(--white)", borderRadius: 12, border: "1px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 12, padding: "13px 16px", cursor: "pointer" }}>
                    <div style={{ fontSize: 18, marginTop: 1 }}>📌</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{note.name}</div>
                      <TagPills tags={note.tags} />
                      {note.lastEdited && <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500, marginTop: 2 }}>Updated {formatDate(note.lastEdited)}</div>}
                    </div>
                    <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                  </div>
                ))}
              </>
            )}
            {pinned && (
              <>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px" }}>📌 Pinned</div>
                <div onClick={() => handleNotebookTap(pinned)} style={{ background: "var(--white)", borderRadius: 16, border: "1px solid var(--border)", padding: "18px 18px 16px", cursor: "pointer", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "var(--sage)" }} />
                  <div style={{ fontSize: 28, marginBottom: 10 }}>📁</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: "var(--charcoal)", marginBottom: 6 }}>{pinned.name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "var(--sage)", background: "var(--sage-muted)", padding: "3px 10px", borderRadius: 20 }}>{pinned.count} notes</span>
                    {pinned.lastEdited && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)" }}>Updated {formatDate(pinned.lastEdited)}</span>}
                  </div>
                  <div style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", fontSize: 22, color: "var(--border)" }}>›</div>
                </div>
              </>
            )}
            {rest.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 4px 2px", marginTop: 4 }}>Notebooks</div>
                {rest.map(nb => (
                  <div key={nb.id} onClick={() => handleNotebookTap(nb)} style={{ background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", cursor: "pointer" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 11, background: "var(--sage-muted)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>📒</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)" }}>{nb.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "var(--sage)", background: "var(--sage-muted)", padding: "2px 8px", borderRadius: 20 }}>{nb.count} notes</span>
                        {nb.lastEdited && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)" }}>Updated {formatDate(nb.lastEdited)}</span>}
                      </div>
                    </div>
                    <span style={{ color: "var(--border)", fontSize: 20 }}>›</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
      <nav style={bottomNav}>
        {NAV_TABS.map(t => (
          <div key={t.id} onClick={() => onTabTap?.(t.id)} style={navItem}>
            <div style={{ fontSize: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: t.id === "essential" ? 800 : 700, color: t.id === "essential" ? "var(--sage)" : "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
            {t.id === "essential" && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--sage)", marginTop: 1 }} />}
          </div>
        ))}
      </nav>
    </div>
  );
}

const backBtn = { width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" };
const bottomNav = { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 };
const navItem = { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" };
const sheetBtn = { background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" };
const sheetLabel = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 8 };
